import { StockLocationType } from 'types/inventory';
import {
  LocationWithStockLots,
  PickListStockLot,
} from 'views/warehouse/MobilePicking/types';
import { WarehouseStockLocation } from 'views/warehouse/WarehouseStockLocationContext';

export interface LocationResults {
  stockLotPickingLocations: LocationWithStockLots[];
  binPickingLocations: LocationWithStockLots[];
}

const sortLocations = (a: LocationWithStockLots, b: LocationWithStockLots) => {
  const pathA = a.path.join('/');
  const pathB = b.path.join('/');
  return pathA.localeCompare(pathB, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

export const buildFacilityPickingLocations = (
  stockLocations: Record<string, WarehouseStockLocation>,
  stockLotsToPick: PickListStockLot[],
  facilityId: string
): LocationResults => {
  if (!Object.keys(stockLocations).length) {
    return { stockLotPickingLocations: [], binPickingLocations: [] };
  }

  const facilityLocation = Object.values(stockLocations).find(
    (location) => location.id === facilityId
  );

  if (!facilityLocation) {
    return { stockLotPickingLocations: [], binPickingLocations: [] };
  }

  const facilityChildren = Object.values(stockLocations).filter(
    (location) => location.parent === facilityLocation.id
  );

  const results = facilityChildren.reduce(
    (acc, child) => {
      const childResults = buildPickingLocations(
        stockLocations,
        stockLotsToPick,
        child.id,
        []
      );
      acc.stockLotPickingLocations.push(
        ...childResults.stockLotPickingLocations
      );
      acc.binPickingLocations.push(...childResults.binPickingLocations);
      return acc;
    },
    { stockLotPickingLocations: [], binPickingLocations: [] } as LocationResults
  );

  return {
    stockLotPickingLocations:
      results.stockLotPickingLocations.sort(sortLocations),
    binPickingLocations: results.binPickingLocations.sort(sortLocations),
  };
};

export const buildPickingLocations = (
  locations: Record<string, WarehouseStockLocation>,
  stockLotGroups: PickListStockLot[],
  rootId: string | null = null,
  path: string[] = []
): LocationResults => {
  const result: LocationResults = {
    stockLotPickingLocations: [],
    binPickingLocations: [],
  };

  const rootLocation = Object.values(locations).find(
    (location) => location.id === rootId
  );

  if (!rootLocation) {
    return result;
  }

  const newPath = [...path, rootLocation.name];

  const locationStockLotGroups = stockLotGroups.filter(
    (group) => group.stockLocation === rootLocation.id
  );

  const childLocations = Object.values(locations).filter(
    (location) => location.parent === rootLocation.id
  );

  if (rootLocation.locationType === StockLocationType.ESD_BIN) {
    const childNodes = childLocations
      .map((childLocation) => {
        const childStockLots = stockLotGroups.filter(
          (group) => group.stockLocation === childLocation.id
        );
        return childStockLots.length > 0
          ? {
              thisLocation: childLocation,
              childStockLocations: [],
              stockLotGroups: childStockLots,
            }
          : null;
      })
      .filter((node): node is NonNullable<typeof node> => node !== null);

    if (locationStockLotGroups.length > 0 || childNodes.length > 0) {
      result.stockLotPickingLocations.push({
        location: rootLocation,
        path: newPath,
        stockLotGroups: locationStockLotGroups,
        childStockLocations: childNodes,
      });
    }

    const allChildStockLots = childLocations.reduce((acc, childLocation) => {
      const childStockLots = stockLotGroups.filter(
        (group) => group.stockLocation === childLocation.id
      );
      return [...acc, ...childStockLots];
    }, [] as PickListStockLot[]);

    const allStockLots = [...locationStockLotGroups, ...allChildStockLots];

    if (allStockLots.length > 0) {
      result.binPickingLocations.push({
        location: rootLocation,
        path: newPath,
        stockLotGroups: allStockLots,
      });
    }
    return result;
  }

  if (locationStockLotGroups.length > 0) {
    result.stockLotPickingLocations.push({
      location: rootLocation,
      path: newPath,
      stockLotGroups: locationStockLotGroups,
    });
  }

  childLocations.forEach((childLocation) => {
    const childResults = buildPickingLocations(
      locations,
      stockLotGroups,
      childLocation.id,
      newPath
    );
    result.stockLotPickingLocations.push(
      ...childResults.stockLotPickingLocations
    );
    result.binPickingLocations.push(...childResults.binPickingLocations);
  });

  return result;
};

// For backward compatibility
export const buildLocationWithStockLots = (
  locations: Record<string, WarehouseStockLocation>,
  stockLotGroups: PickListStockLot[],
  rootId: string | null = null,
  path: string[] = []
): LocationWithStockLots[] =>
  buildPickingLocations(locations, stockLotGroups, rootId, path)
    .stockLotPickingLocations;

export const buildBinPickingLocations = (
  locations: Record<string, WarehouseStockLocation>,
  stockLotGroups: PickListStockLot[],
  rootId: string | null = null,
  path: string[] = []
): LocationWithStockLots[] =>
  buildPickingLocations(locations, stockLotGroups, rootId, path)
    .binPickingLocations;
